import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, RotateCcw } from 'lucide-react'
import { Panel, Chip, Button, Banner, StatTile, ActivityFeed, Select, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, put } from '../lib/api'
import { useSession } from '../lib/session'
import { ago, money, num, title, dateShort } from '../lib/format'
import { Icon } from '../components/Icon'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { URGENCY } from './MyLeads'
import { duration } from './Dashboard'
import { usd } from './Costs'
import type { Question } from '../lib/types'
import { ScoreChip } from '../components/ScoreChip'

type Detail = {
  lead: {
    id: string; post_url: string | null; stage: string; copies: number; author_display: string | null; body_text: string | null
    text_truncated: boolean; first_seen_at: string; is_lead: boolean | null; confidence: string | null; screen_reason: string | null
    summary: string | null; urgency: string | null; area_text: string | null; answers: Record<string, string> | null; lead_score: number | null
    route_note: string | null; last_error: string | null; attempts: number; body_purged_at: string | null
    group_name: string | null; category_name: string | null; area_name: string | null; suggested_category: string | null
    tier_id: string | null; tier_name: string | null; tier_suggested_name: string | null; tier_reason: string | null
    tier_set_at: string | null; tier_set_by_name: string | null; tier_locked: boolean; tier_price_cents: number | null
    graded_at: string | null; grade_due_at: string | null; grade_reason: string | null; grade_error: string | null; grade_tier_name: string | null
    lane_id: string | null; lane_name: string | null; lane_suggested_name: string | null; lane_urgent: boolean
    lane_set_at: string | null; lane_set_by_name: string | null; job_type_name: string | null
    location_status: 'resolved' | 'unknown' | 'settled' | null; location_text: string | null; location_source: string | null
    location_confidence: string | null; zone_id: string | null; zone_name: string | null; location_asked_at: string | null
    person_lives_in: string | null; person_from: string | null; location_answer: string | null
    muse_hints: { filter?: string | null; kind?: string | null; tier?: string | null; location?: string | null } | null
  }
  zone_routing?: boolean
  zones?: { id: string; name: string }[]
  tiers: { id: string; name: string; price_cents: number }[]
  lanes: { id: string; name: string; description: string; price_cents: number | null }[]
  matches: { subscriber_id: string; name: string; score: number; fit: boolean; reason: string; overridden_at: string | null; overridden_by_name: string | null; sold_to: boolean }[]
  view: string; newer_id: string | null; older_id: string | null
  questions: Question[]
  group_reps: { id: string; name: string }[]
  assignments: {
    id: string; status: 'open' | 'responded' | 'skipped'; match: string; created_at: string; opened_at: string | null; responded_at: string | null
    reply_note: string | null; skip_reason: string | null; has_image: boolean; subscriber_notified_at: string | null
    details: string | null; details_at: string | null
    conversation: { id: string; has_image: boolean; note: string | null; created_at: string }[]; conversation_closed_at: string | null
    buyers: { id: string; name: string; billed_cents: number | null; outcome: 'contacted' | 'not_yet' | null; notified_at: string | null
      list_cents: number | null; prepaid_credit_cents: number; overage_cents: number; dispute_status: string | null; refunded_at: string | null }[]; email: { id: string; status: 'pending' | 'sent' | 'failed' | 'skipped'; error: string | null; sent_at: string | null; to: string | null } | null
    rep_id: string; rep_name: string; rep_phone: string | null
    subscriber_id: string | null; subscriber_name: string | null; subscriber_pitch: string | null; subscriber_plan: string | null
    reply_after_s: number | null
  }[]
  emails: { id: string; received_at: string; kind: string; inbox: string; notes: string[] | null }[]
  ai_calls: { stage: string; model: string; ok: boolean; error: string | null; latency_ms: number | null; created_at: string; input_tokens?: number; output_tokens?: number; cost_micros?: string }[]
  timeline: { at: string; kind: string; text: string; tone: 'pos' | 'warn' | 'neg' | null }[]
  post_to_first_reply_s: number | null
}

const STAGE: Record<string, { label: string; tone?: 'pos' | 'warn' | 'neg' | 'accent' }> = {
  queued: { label: 'Waiting to screen', tone: 'accent' },
  no_reps: { label: 'Held: no reps in the group', tone: 'warn' },
  too_old: { label: 'Too old: arrived before the group had a rep' },
  not_lead: { label: 'Not a lead' },
  lead: { label: 'Being routed', tone: 'accent' },
  routed: { label: 'Routed' },
  failed: { label: 'Screening failed', tone: 'neg' },
}
const MATCH: Record<string, string> = {
  area: 'Matched on category and area',
  category_only: 'Matched on category; the post names no area we know',
  no_subscriber: 'Every rep in the group is told; no matching business',
  held_back: 'Held back by the AI, sent to every rep in the group anyway',
}
const when = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
const initials = (n: string) => n.split(' ').map((x) => x[0]).join('').slice(0, 2)

export default function LeadPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const view = params.get('view') || 'leads'
  const nav = useNavigate()
  const toast = useToast()
  const { me } = useSession()
  const qs = new URLSearchParams(params); qs.set('view', view)
  const { data, error, reload } = useApi<Detail>(`/api/leads/${id}?${qs}`)

  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const { lead: l, assignments: as } = data
  const back = `/leads?${qs}`
  const go = (target: string | null) => target && nav(`/leads/${target}?${qs}`)
  const replied = as.filter((a) => a.status === 'responded')
  const stage = STAGE[l.stage] || { label: l.stage }
  const spec = me?.user.role === 'spec'

  const requeue = async () => {
    try { await post(`/api/leads/${l.id}/requeue`, {}); toast('Sent back to screening', 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }

  return (
    <div className="lead-page">
      <div className="lead-nav">
        <Link to={back} className="btn btn-ghost"><Icon of={ArrowLeft} size={15} /> Lead board</Link>
        <div className="row-tight">
          <Button variant="secondary" onClick={() => go(data.newer_id)} disabled={!data.newer_id} aria-label="Newer lead"><Icon of={ChevronLeft} size={15} /> Newer</Button>
          <Button variant="secondary" onClick={() => go(data.older_id)} disabled={!data.older_id} aria-label="Older lead">Older <Icon of={ChevronRight} size={15} /></Button>
        </div>
      </div>

      <header className="lead-hero">
        <div className="lead-hero-chips">
          <Chip tone={l.stage === 'routed' ? (replied.length ? 'pos' : as.length ? 'accent' : 'warn') : stage.tone} dot>
            {l.stage === 'routed' ? (replied.length ? `Replied${as.length > 1 ? ` ${replied.length} of ${as.length}` : ''}` : as.length ? 'Waiting on a rep' : 'Nobody to send it to') : stage.label}
          </Chip>
          {l.urgency && URGENCY[l.urgency] && <Chip tone={URGENCY[l.urgency].tone} dot={Boolean(URGENCY[l.urgency].tone)}>{URGENCY[l.urgency].label}</Chip>}
          {l.category_name && <Chip>{l.category_name}</Chip>}
          {l.area_name ? <Chip>{l.area_name}</Chip> : l.is_lead ? <Chip tone="warn">Area unclear</Chip> : null}
        </div>
        <h1 className="lead-title">{l.summary || l.body_text?.split(/(?<=[.?!])\s/)[0] || 'A post'}</h1>
        <p className="lead-sub">{l.group_name || 'Unknown group'} · {l.author_display || 'a member'} · seen {ago(l.first_seen_at)} · {num(l.copies)} notification email{l.copies === 1 ? '' : 's'}</p>
        <div className="row-tight" style={{ flexWrap: 'wrap' }}>
          {l.post_url && <a className="btn btn-primary" href={l.post_url} target="_blank" rel="noopener noreferrer"><Icon of={ExternalLink} size={15} /> Open on Facebook</a>}
          {l.stage !== 'routed' && l.body_text && <Button variant="secondary" onClick={requeue}><Icon of={RotateCcw} size={15} /> Screen again</Button>}
        </div>
      </header>

      {l.last_error && <Banner tone="neg" title={`Screening failed ${l.attempts} time${l.attempts === 1 ? '' : 's'}`}>{l.last_error}</Banner>}

      <div className="grid-tiles" style={{ margin: 'var(--s-6) 0' }}>
        <StatTile focal label="Post to first reply" value={data.post_to_first_reply_s == null ? (as.length ? 'not yet' : 'n/a') : duration(data.post_to_first_reply_s)} meta={replied.length ? `first reply ${ago(replied.map((a) => a.responded_at!).sort()[0])}` : as.length ? 'nobody has replied' : 'not sent to anyone'} />
        <StatTile label="Replies" value={as.length ? `${replied.length} of ${as.length}` : '0'} meta={`${as.filter((a) => a.status === 'skipped').length} skipped`} progress={as.length ? (replied.length / as.length) * 100 : undefined} />
        <StatTile label="Reps in the group" value={`${data.group_reps.length} of 4`} meta={data.group_reps.map((r) => r.name.split(' ')[0]).join(', ') || 'none'} />
        <StatTile label="Screening" value={l.is_lead == null ? 'pending' : l.is_lead ? 'Request' : 'Not a request'} meta={l.confidence ? `${l.confidence} confidence` : ''} />
      </div>

      <div className="wide-narrow">
        <div className="stack">
          <Panel>
            <div className="panel-header"><h2 className="panel-title">The post</h2><span className="muted">as the notification email carried it</span></div>
            {l.body_text ? <p className="lead-post">{l.body_text}</p> : <p className="muted">The text was deleted under the retention policy.</p>}
            {l.text_truncated && <p className="post-cut">The email cut this post off. The rest is on Facebook.</p>}
          </Panel>

          {data.zone_routing && l.is_lead && <ZonePanel lead={l} zones={data.zones || []} onChanged={reload} />}
          {data.tiers.length > 0 && l.is_lead && <TierPanel lead={l} tiers={data.tiers} lanes={data.lanes || []} onChanged={reload} />}
          {data.matches.length > 0 && <MatchPanel postId={l.id} matches={data.matches} onChanged={reload} />}

          <Panel>
            <div className="panel-header"><h2 className="panel-title">What the screening read</h2></div>
            <div className="read-grid">
              <div><span className="read-label">Why</span><span>{l.screen_reason || 'not screened yet'}</span></div>
              <div><span className="read-label">Category</span><span>{l.category_name || (l.suggested_category ? `Uncategorized: they asked for ${l.suggested_category.toLowerCase()}` : 'none')}</span></div>
              <div><span className="read-label">Area</span><span>{l.area_name ? `${l.area_name}${l.area_text && l.area_text.toLowerCase() !== l.area_name.toLowerCase() ? `, written "${l.area_text}"` : ''}` : l.area_text ? `"${l.area_text}", not an area we cover` : 'not stated'}</span></div>
              <div><span className="read-label">Timing</span><span>{l.urgency ? URGENCY[l.urgency]?.label : 'not read'}</span></div>
              {(l.muse_hints?.kind || l.muse_hints?.filter) && <div><span className="read-label">Muse's label</span><span>{[l.muse_hints.filter, l.muse_hints.kind].filter(Boolean).join(': ')}{l.muse_hints.tier ? `, tier ${l.muse_hints.tier}` : ''}</span></div>}
              <div><span className="read-label">Score</span><span>{l.lead_score != null ? <ScoreChip score={l.lead_score} /> : 'not scored'}</span></div>
            </div>
            {data.questions.length > 0 && l.answers && (
              <div className="answer-list">
                {data.questions.map((q) => {
                  const a = l.answers?.[q.key] || 'unclear'
                  return <div key={q.key}><span>{q.text}</span><Chip tone={a === 'unclear' ? undefined : 'accent'} dot={a !== 'unclear'}>{title(a)}</Chip></div>
                })}
              </div>
            )}
          </Panel>

          <section>
            <div className="section-title"><h2 className="panel-title">Sent to</h2><span className="muted">{as.length ? `${as.length} rep${as.length === 1 ? '' : 's'}` : ''}</span></div>
            {as.length === 0 ? (
              <Banner tone="warn" title="Nobody to send it to">
                {l.route_note || (l.stage === 'routed' ? 'No rep in this group represents a matching business.' : 'It has not been routed.')}
                {data.group_reps.length > 0 && <> Reps in this group: {data.group_reps.map((r) => r.name).join(', ')}.</>}
              </Banner>
            ) : (
              <div className="assign-grid">
                {as.map((a) => (
                  <article key={a.id} className="assign-card">
                    <div className="spread">
                      <span className="avatar-cell"><span className="avatar" aria-hidden="true">{initials(a.rep_name)}</span><span><strong>{a.rep_name}</strong><div className="muted">notified {ago(a.created_at)}</div></span></span>
                      <Chip tone={a.status === 'responded' ? 'pos' : a.status === 'open' ? 'accent' : undefined} dot>{a.status === 'responded' ? 'Replied' : a.status === 'open' ? 'Waiting' : 'Skipped'}</Chip>
                    </div>
                    {a.buyers?.length ? (
                      <div className="recommend">
                        <span className="s-label muted">{a.buyers.length > 1 ? `Recommending ${a.buyers.length}, in one reply` : 'Recommending'}</span>
                        {a.buyers.map((b) => (
                          <span key={b.id} className="recommend-name">
                            {b.name}
                            {b.billed_cents != null && <span className="muted" style={{ fontSize: 'var(--t-ui)' }}> · {money(b.billed_cents)}</span>}
                          </span>
                        ))}
                      </div>
                    ) : <p className="muted" style={{ margin: 0 }}>No matching business for this rep.</p>}
                    <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>{MATCH[a.match] || a.match}</span>
                    <ol className="mini-steps">
                      <li className="done">Notified <span className="muted">{when(a.created_at)}</span></li>
                      <li className={a.opened_at ? 'done' : ''}>Opened <span className="muted">{a.opened_at ? when(a.opened_at) : 'not yet'}</span></li>
                      <li className={a.status === 'responded' ? 'done' : a.status === 'skipped' ? 'skipped' : ''}>
                        {a.status === 'skipped' ? 'Skipped' : 'Replied'} <span className="muted">{a.responded_at ? `${when(a.responded_at)}, after ${duration(a.reply_after_s)}` : a.status === 'skipped' ? a.skip_reason : 'not yet'}</span>
                      </li>
                    </ol>
                    {a.reply_note && <p className="assign-note">"{a.reply_note}"</p>}
                    {a.details && <div className="assign-details"><span className="s-label muted">What {a.rep_name.split(' ')[0]} learned{a.details_at ? `, ${when(a.details_at)}` : ''}</span><p>{a.details}</p></div>}
                    {a.has_image && (
                      <a href={`/api/assignments/${a.id}/image`} target="_blank" rel="noopener noreferrer" className="shot-link" title="Open the full screenshot">
                        <img src={`/api/assignments/${a.id}/image`} alt={`Screenshot of ${a.rep_name}'s reply`} />
                      </a>
                    )}
                    {a.conversation?.length > 0 && (
                      <div className="assign-details">
                        <span className="s-label muted">The conversation after{a.conversation_closed_at ? `, closed ${when(a.conversation_closed_at)}` : ', still open'}</span>
                        {a.conversation.map((c) => (
                          <div key={c.id}>
                            {c.has_image && <a href={`/api/conversation/${c.id}/image`} target="_blank" rel="noopener noreferrer" className="shot-link"><img src={`/api/conversation/${c.id}/image`} alt="Conversation screenshot" /></a>}
                            {c.note && <p>{c.note} <span className="muted">{when(c.created_at)}</span></p>}
                          </div>
                        ))}
                      </div>
                    )}
                    {a.buyers?.length > 0 && a.status === 'responded' && (
                      <div className="assign-email">
                        {a.email?.status === 'sent' ? <Chip tone="pos" dot>Emailed {ago(a.email.sent_at)}</Chip>
                          : a.email?.status === 'pending' ? <Chip dot>Email waiting to send</Chip>
                          : a.email ? <><Chip tone={a.email.status === 'failed' ? 'neg' : 'warn'} dot>Email not sent</Chip><span className="muted">{a.email.error}</span></>
                          : <span className="muted">No email was written for this reply.</span>}
                        {a.buyers.map((b) => (
                          b.outcome === 'contacted' ? <Chip key={b.id} tone="pos">They reached out, says {b.name}</Chip>
                            : b.outcome === 'not_yet' ? <Chip key={b.id}>Not contacted yet, says {b.name}</Chip>
                            : null
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="stack">
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Timeline</h2></div>
            <ActivityFeed entries={data.timeline.map((t) => ({ meta: when(t.at), body: t.text, tone: t.tone || undefined }))} />
          </Panel>
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Notification emails</h2><span className="muted">{data.emails.length}</span></div>
            <ul className="plain-list">
              {data.emails.map((e) => (
                <li key={e.id}><span>{e.inbox}</span><span className="muted nowrap">{when(e.received_at)}</span></li>
              ))}
            </ul>
            {data.emails.length > 1 && <p className="muted" style={{ fontSize: 'var(--t-ui)', margin: 'var(--s-4) 0 0' }}>One post, {data.emails.length} copies. It was screened once.</p>}
          </Panel>
          <Panel>
            <div className="panel-header"><h2 className="panel-title">AI</h2>{spec && <span className="muted">SPEC sees cost</span>}</div>
            {data.ai_calls.length === 0 ? <p className="muted" style={{ margin: 0 }}>No model calls for this post.</p> : (
              <ul className="plain-list">
                {data.ai_calls.map((c, i) => (
                  <li key={i}>
                    <span>{c.stage === 'screen' ? 'Screened' : 'Details read'} <span className="muted">{c.model === 'fake' ? 'by demo rules' : c.model.replace(/-\d{8}$/, '')}</span>{!c.ok && <> <Chip tone="neg">failed</Chip></>}</span>
                    <span className="muted nowrap">{spec && c.cost_micros != null ? `${usd(c.cost_micros)} · ` : ''}{c.latency_ms != null ? `${(c.latency_ms / 1000).toFixed(1)} s` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}

/**
 * The lead's tier and lane: what the model chose and why, and a way to change
 * either until the lead is first sold. After that the price is fixed on every sale.
 * Tier is the conversation; job tier (a lane in the code) is the size of the job.
 */
function TierPanel({ lead: l, tiers, lanes, onChanged }: { lead: Detail['lead']; tiers: Detail['tiers']; lanes: Detail['lanes']; onChanged: () => void }) {
  const toast = useToast()
  const change = async (tierId: string) => {
    if (!tierId || tierId === l.tier_id) return
    try {
      await put(`/api/leads/${l.id}/tier`, { tier_id: tierId })
      toast('Tier changed', 'pos')
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const changeLane = async (laneId: string) => {
    if (!laneId || laneId === l.lane_id) return
    try {
      await put(`/api/leads/${l.id}/lane`, { lane_id: laneId })
      toast('Job tier changed', 'pos')
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const changedByPerson = Boolean(l.tier_set_by_name)
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">{lanes.length ? 'Conversation tier and job tier' : 'Tier'}</h2>
        <span className="muted">{l.tier_price_cents != null ? `${money(l.tier_price_cents)} a buyer` : ''}</span>
      </div>
      <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--s-4)' }}>
        <Chip tone="accent">{l.tier_name || 'No tier'}</Chip>
        {changedByPerson
          ? <span className="muted">Set by {l.tier_set_by_name} {ago(l.tier_set_at)}{l.tier_suggested_name ? `; screening said ${l.tier_suggested_name}` : ''}</span>
          : <span className="muted">Chosen by screening</span>}
      </div>
      {l.tier_reason && <p style={{ marginTop: 0 }}><span className="read-label">Why</span> {l.tier_reason}</p>}
      <p className="muted" style={{ marginTop: 0 }}>
        {l.graded_at
          ? `Graded from the conversation ${ago(l.graded_at)}${l.grade_tier_name && l.grade_tier_name !== l.tier_name ? `; the model said ${l.grade_tier_name}, your tier stood` : ''}.`
          : l.grade_due_at
            ? `Graded from the conversation when the reps are done, or by ${new Date(l.grade_due_at).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}. Until then this tier is read from the post.`
            : 'Read from the post. Graded again from the conversation once a rep replies.'}
        {l.grade_error && !l.graded_at ? ` Last grading attempt failed: ${l.grade_error}` : ''}
      </p>
      {l.tier_locked
        ? <p className="muted" style={{ marginBottom: 0 }}>Sold already, so the tier and its price are fixed.</p>
        : (
          <label className="row-tight" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Change to</span>
            <Select aria-label="Change tier" value={l.tier_id || ''} onChange={(e) => change(e.target.value)}>
              {!l.tier_id && <option value="">Choose a tier</option>}
              {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}, {money(t.price_cents)}</option>)}
            </Select>
          </label>
        )}
      {lanes.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 'var(--s-4)', paddingTop: 'var(--s-4)' }}>
          <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--s-3)' }}>
            <Chip>{l.lane_name ? `Job tier: ${l.lane_name}` : 'No job tier'}</Chip>
            <span className="muted">
              {l.lane_set_by_name
                ? `Set by ${l.lane_set_by_name} ${ago(l.lane_set_at)}${l.lane_suggested_name ? `; screening said ${l.lane_suggested_name}` : ''}`
                : l.job_type_name
                  ? `${l.job_type_name}${l.lane_urgent ? ', moved up one because they need it now' : ''}`
                  : "No job type on this lead, so it is priced at its trade's job tier"}
            </span>
          </div>
          {!l.tier_locked && (
            <label className="row-tight" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Change to</span>
              <Select aria-label="Change job tier" value={l.lane_id || ''} onChange={(e) => changeLane(e.target.value)}>
                {!l.lane_id && <option value="">Choose a job tier</option>}
                {lanes.map((x) => <option key={x.id} value={x.id}>{x.name}{x.price_cents != null ? `, ${money(x.price_cents)}` : ''}</option>)}
              </Select>
            </label>
          )}
        </div>
      )}
    </Panel>
  )
}

/**
 * The AI's read of which clients suit this lead, from their profiles. A client
 * it called not a fit was left out; an admin can send it to them anyway.
 */
function MatchPanel({ postId, matches, onChanged }: { postId: string; matches: Detail['matches']; onChanged: () => void }) {
  const toast = useToast()
  const include = async (id: string) => {
    try {
      const r = await post<{ note: string }>(`/api/leads/${postId}/matches/${id}/include`, {})
      toast(r.note || 'Sent', 'pos'); onChanged()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Client fit</h2><span className="muted">from their profiles</span></div>
      <div className="stack-tight">
        {matches.map((m) => (
          <div key={m.subscriber_id} className="stack-tight" style={{ gap: 'var(--s-2)', borderTop: '1px solid var(--border)', paddingTop: 'var(--s-3)' }}>
            <div className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong>{m.name}</strong>
              <span className="row-tight" style={{ alignItems: 'center' }}>
                <Chip tone={m.fit ? (m.score >= 70 ? 'pos' : undefined) : 'neg'} dot>{m.fit ? `${m.score} fit` : 'Not a fit'}</Chip>
                {m.sold_to && <Chip tone="accent">Sent</Chip>}
              </span>
            </div>
            <span className="muted">{m.reason}{m.overridden_by_name ? ` Sent anyway by ${m.overridden_by_name}.` : ''}</span>
            {!m.fit && !m.sold_to && <div><Button variant="secondary" onClick={() => include(m.subscriber_id)}>Send to them anyway</Button></div>}
          </div>
        ))}
      </div>
    </Panel>
  )
}

const SOURCE: Record<string, string> = {
  lives_in: 'their profile says they live there', answer: 'they told the rep', post: 'they named it in the post', muse: "the rep's Muse noted it",
  from: 'their profile says they are from there', group: "the group's home zone", admin: 'set by a person',
}

/**
 * Where the person is and which zone that puts them in (terms 2.4, 2.5). A
 * lead the system was not sure about waits here, unsold, until someone picks
 * the zone or asks the rep to ask.
 */
function ZonePanel({ lead, zones, onChanged }: { lead: Detail['lead']; zones: { id: string; name: string }[]; onChanged: () => void }) {
  const toast = useToast()
  const [zone, setZone] = useState(lead.zone_id || '')
  const [busy, setBusy] = useState(false)
  const settle = async () => {
    setBusy(true)
    try {
      const r = await post<{ zone: string; business: string | null }>(`/api/leads/${lead.id}/location`, { zone_id: zone })
      toast(r.business ? `${r.zone}: sent to ${r.business}` : `${r.zone}: no business holds this trade there, so it is on the Unassigned list`, r.business ? 'pos' : 'neutral')
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const ask = async () => {
    try { await post(`/api/leads/${lead.id}/ask-location`, {}); toast('The rep has been asked to ask where they are', 'pos'); onChanged() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const unknown = lead.location_status === 'unknown'
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Zone</h2>
        {unknown ? <Chip tone="warn" dot>Location unknown</Chip> : lead.zone_name ? <Chip tone="accent" dot>{lead.zone_name}</Chip> : <Chip>Not located yet</Chip>}
      </div>
      <div className="read-grid">
        <div><span className="read-label">How we know</span><span>{lead.location_source ? `${SOURCE[lead.location_source] || lead.location_source}${lead.location_text ? `: "${lead.location_text}"` : ''}` : 'no place in the lead matches a zone'}</span></div>
        {lead.location_confidence && <div><span className="read-label">How sure</span><span>{lead.location_confidence}</span></div>}
        {lead.person_lives_in && <div><span className="read-label">Lives in</span><span>{lead.person_lives_in}</span></div>}
        {lead.person_from && <div><span className="read-label">From</span><span>{lead.person_from}</span></div>}
        {lead.location_answer && <div><span className="read-label">Their answer</span><span>{lead.location_answer}</span></div>}
      </div>
      {unknown && (
        <div className="stack-tight" style={{ marginTop: 'var(--s-4)' }}>
          <p className="muted" style={{ margin: 0 }}>Not sent and not billed until the zone is settled.{lead.zone_name ? ` The best guess is ${lead.zone_name}.` : ''}</p>
          <div className="row-tight" style={{ flexWrap: 'wrap' }}>
            <Select aria-label="Zone" value={zone} onChange={(e) => setZone(e.target.value)}>
              <option value="">Choose the zone</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </Select>
            <Button onClick={settle} disabled={!zone || busy}>Settle and send</Button>
            <Button variant="ghost" onClick={ask}>{lead.location_asked_at ? 'Ask the rep again' : 'Ask the rep to ask'}</Button>
          </div>
          {lead.location_asked_at && <span className="muted">Rep asked {dateShort(lead.location_asked_at)}.</span>}
        </div>
      )}
    </Panel>
  )
}
